import React, { useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Sheet, CancelCrossIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import { fetchProject } from "~/store/actions/actions.projects.jsx";
import { fetchTaskLists } from "~/store/actions/actions.taskLists.jsx";
import { addTask } from "~/store/actions/actions.tasks.jsx";
import { getProjectMembersById } from "~/store/selectors/selector.projects.jsx";
import { getLoggedUserId } from "~/store/selectors/selector.users.jsx";
import TaskFormContainer from "./TaskFormContainer.jsx";
import { showErrorToast } from "../Toast";

const CreateTaskModal = ({ open, onClose, projectId, presetNoAssignee, presetAssigneeId, dueOn }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loggedUserId = useSelector(getLoggedUserId);
  const projectMembers = useSelector((state) => getProjectMembersById(state, projectId));

  useEffect(() => {
    if (projectId > 0 && open) {
      dispatch(fetchTaskLists(projectId));
      dispatch(fetchProject(projectId));
    }
  }, [dispatch, projectId, open]);

  const controls = useMemo(
    () => [
      {
        tooltip: t("Close"),
        onClick: onClose,
        icon: <CancelCrossIcon />,
        className: "task_modal_close_icon",
      },
    ],
    [onClose, t]
  );

  const handleSubmit = useCallback(
    (attributes) => {
      dispatch(addTask(projectId, attributes)).then((action) => {
        const { response = {} } = action;
        const { result = {} } = response;
        const { single: taskId } = result;

        if (taskId) {
          typeof onClose === "function" && onClose();
          navigate(`?modal=Task-${taskId}-${projectId}`);
        } else {
          showErrorToast(t("Failed to create task"));
        }
      });
    },
    [dispatch, projectId, t, onClose, navigate]
  );

  const assigneeId = useMemo(() => {
    if (presetAssigneeId && projectMembers.includes(presetAssigneeId)) {
      return presetAssigneeId;
    }
    if (!presetNoAssignee && projectMembers.length && projectMembers.indexOf(loggedUserId) > -1) {
      return loggedUserId;
    }
    return undefined;
  }, [loggedUserId, presetAssigneeId, presetNoAssignee, projectMembers]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      position="right"
      animation="right"
      mode="stretch"
      className="task-modal-container"
      controls={controls}
    >
      <div className="tw-p-8 sm:tw-pr-2">
        <TaskFormContainer
          projectId={projectId}
          submitButtonLabel={t("Add Task")}
          onCancel={onClose}
          onSubmit={handleSubmit}
          assigneeId={assigneeId}
          dueOn={dueOn}
        />
      </div>
    </Sheet>
  );
};

CreateTaskModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  projectId: PropTypes.number.isRequired,
  presetNoAssignee: PropTypes.bool,
  presetAssigneeId: PropTypes.number,
  dueOn: PropTypes.number,
};

CreateTaskModal.defaultProps = {
  open: false,
  presetNoAssignee: false,
};

CreateTaskModal.displayName = "CreateTaskModal";

export default CreateTaskModal;
